import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { InfoCircle } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { useControllableState } from '../internal/useControllableState';
import styles from './Form.module.css';

export interface ChoiceOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface GroupFieldProps {
  /** Group label rendered as the <legend>. */
  label: ReactNode;
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

export interface RadioGroupProps
  extends GroupFieldProps, Omit<HTMLAttributes<HTMLFieldSetElement>, 'onChange' | 'defaultValue'> {
  options: ChoiceOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
  orientation?: 'vertical' | 'horizontal';
}

export function RadioGroup({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  disabled = false,
  options,
  value: valueProp,
  defaultValue = '',
  onChange,
  name: nameProp,
  orientation = 'vertical',
  className,
  ...rest
}: RadioGroupProps) {
  const baseId = useId();
  const name = nameProp ?? `radio-${baseId}`;
  const hintId = hint ? `${baseId}-hint` : undefined;
  const errorId = error ? `${baseId}-error` : undefined;
  const [value, setValue] = useControllableState(valueProp, defaultValue, onChange);

  return (
    <fieldset
      className={cx(styles.group, className)}
      aria-describedby={cx(hintId, errorId) || undefined}
      disabled={disabled || undefined}
      {...rest}
    >
      <legend className={cx(styles.label, styles.legend, hideLabel && 'sr-only')}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </legend>
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      <div className={cx(styles.choices, orientation === 'horizontal' && styles.choicesRow)}>
        {options.map((option) => {
          const optionId = `${baseId}-${option.value}`;
          const descId = option.description ? `${optionId}-desc` : undefined;
          return (
            <div
              key={option.value}
              className={cx(styles.choice, (disabled || option.disabled) && styles.choiceDisabled)}
            >
              <span className={styles.checkWrap}>
                <input
                  type="radio"
                  id={optionId}
                  name={name}
                  value={option.value}
                  className={styles.checkInput}
                  checked={value === option.value}
                  disabled={option.disabled}
                  required={required || undefined}
                  aria-describedby={descId}
                  onChange={() => setValue(option.value)}
                />
                <span className={cx(styles.checkBox, styles.radioBox)} aria-hidden="true" />
              </span>
              <span className={styles.choiceText}>
                <label htmlFor={optionId} className={styles.choiceLabel}>
                  {option.label}
                </label>
                {option.description && (
                  <span id={descId} className={styles.choiceDescription}>
                    {option.description}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className={styles.error}>
          <InfoCircle size={14} color="currentColor" variant="Bold" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  );
}
